// frontend/src/components/ModuleContent.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle, Clock, Target, BookOpen, Play } from 'lucide-react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { apiUrl } from '../config/api';

interface User {
  id: string;
  email: string;
  fullName: string;
  company: string;
  role: string;
  status: string;
}

interface ModuleContentProps {
  user: User;
  onLogout: () => void;
}

interface Lesson {
  lessonId: number;
  title: string;
  description: string;
  duration: string;
  order: number;
  progress: {
    status: 'not_started' | 'in_progress' | 'completed';
    timeSpent: number;
    lastAccessedAt: string | null;
    completedAt: string | null;
  };
}

interface Module {
  moduleId: number;
  title: string;
  description: string;
  duration: string;
  imageUrl: string;
}

export function ModuleContent({ user, onLogout }: ModuleContentProps) {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState<Module | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchModuleContent = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(apiUrl(`/api/modules/${moduleId}`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch module content');
        }

        const data = await response.json();
        setModule(data.module);
        setLessons(data.lessons);
      } catch (error) {
        console.error('Error fetching module:', error);
        setError('Failed to load module content');
      } finally {
        setLoading(false);
      }
    };

    if (moduleId) {
      fetchModuleContent();
    }
  }, [moduleId]);

  const handleLessonClick = (lessonId: number) => {
    navigate(`/module/${moduleId}/lesson/${lessonId}`);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Play className="w-5 h-5 text-orange-500" />;
      default:
        return <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />;
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'var(--white)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Navbar user={user} onLogout={onLogout} />
        <main style={{ flex: 1 }}>
          <div className="container">
            <div className="loading-container">
              <div className="loading-spinner">Loading...</div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !module) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'var(--white)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Navbar user={user} onLogout={onLogout} />
        <main style={{ flex: 1 }}>
          <div className="container">
            <div className="module-card">
              <h2>Error Loading Module</h2>
              <p>{error || 'Module not found'}</p>
              <Link to="/" className="try-now-button">
                Return to Dashboard
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const completedLessons = lessons.filter(l => l.progress.status === 'completed').length;
  const progressPercentage = lessons.length > 0 ? (completedLessons / lessons.length) * 100 : 0;

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'var(--white)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Navbar user={user} onLogout={onLogout} />
      <main style={{ flex: 1 }}>
        <div className="container">
          <Link to={user ? `/user/${user.id}/dashboard` : "/"} className="back-link">
            <ChevronLeft />
            Back to Dashboard
          </Link>

          <div className="module-card course-overview">
            <div className="course-header">
              <div className="module-badge">
                Module {moduleId}
              </div>
              <h1>{module.title}</h1>
              <p className="course-description">{module.description}</p>
            </div>

            <div className="course-stats">
              <div className="stat-item">
                <Clock className="stat-icon" />
                <div className="stat-content">
                  <span className="stat-label">Duration</span>
                  <span className="stat-value">{module.duration}</span>
                </div>
              </div>
              <div className="stat-item">
                <Target className="stat-icon" />
                <div className="stat-content">
                  <span className="stat-label">Progress</span>
                  <span className="stat-value">{Math.round(progressPercentage)}%</span>
                </div>
              </div>
              <div className="stat-item">
                <BookOpen className="stat-icon" />
                <div className="stat-content">
                  <span className="stat-label">Lessons</span>
                  <span className="stat-value">{completedLessons}/{lessons.length}</span>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="progress-bar-container" style={{ margin: '2rem 0' }}>
              <div className="progress-bar" style={{ width: '100%', height: '8px', backgroundColor: '#eee', borderRadius: '4px', overflow: 'hidden' }}>
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${progressPercentage}%`, 
                    height: '100%', 
                    backgroundColor: 'var(--orange)', 
                    transition: 'width 0.3s ease' 
                  }} 
                />
              </div>
            </div>
          </div>

          <h2 className="section-title">Course Lessons</h2>
          <div className="lessons-grid" style={{ display: 'grid', gap: '1.5rem' }}>
            {lessons.map((lesson, index) => (
              <div 
                key={lesson.lessonId}
                onClick={() => handleLessonClick(lesson.lessonId)}
                className="lesson-card"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  padding: '1.5rem',
                  background: 'white',
                  border: '1px solid #eee',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <div className="lesson-status" style={{ flexShrink: 0 }}>
                  {getStatusIcon(lesson.progress.status)}
                </div>
                
                <div className="lesson-number" style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '8px', 
                  background: lesson.progress.status === 'completed' ? 'var(--orange)' : '#f3f4f6',
                  color: lesson.progress.status === 'completed' ? 'white' : '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}>
                  {index + 1}
                </div>

                <div className="lesson-content" style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--dark)', marginBottom: '0.5rem' }}>
                    {lesson.title}
                  </h3>
                  <p style={{ color: 'var(--gray)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    {lesson.description}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Clock style={{ width: '14px', height: '14px' }} />
                      {lesson.duration}
                    </span>
                    {lesson.progress.timeSpent > 0 && (
                      <span>Time spent: {lesson.progress.timeSpent}min</span>
                    )}
                    {lesson.progress.status === 'completed' && lesson.progress.completedAt && (
                      <span>Completed: {new Date(lesson.progress.completedAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>

                <div className="lesson-action" style={{ flexShrink: 0 }}>
                  <button 
                    className="lesson-button"
                    style={{
                      padding: '0.5rem 1rem',
                      background: lesson.progress.status === 'completed' ? 'var(--orange)' : 'var(--dark)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    {lesson.progress.status === 'completed' ? 'Review' : 
                     lesson.progress.status === 'in_progress' ? 'Continue' : 'Start'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}